import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  FiAlertCircle,
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiCheckCircle,
  FiChevronDown,
  FiCircle,
  FiFileText,
  FiImage,
  FiLayers,
  FiPackage,
  FiRefreshCw,
  FiRotateCcw,
  FiSend,
  FiStar,
  FiTag,
  FiTrash2,
  FiUploadCloud,
  FiVideo,
  FiX,
} from "react-icons/fi";

import api from "../api/axios";
import { toast } from "react-toastify";
import "../assets/styles/product-form.css";

const MEDIA_ACCEPT =
  "image/jpeg,image/jpg,image/png,image/webp,video/mp4,video/quicktime,video/webm";

const MEDIA_EXTENSION_PATTERN =
  /\.(jpe?g|png|webp|mp4|mov|webm)$/i;

const initialForm = {
  category_id: "",
  sub_category_id: "",
  tax_class_id: "",
  product_name: "",
  sku: "",

  short_description: "",
  description: "",
  product_info: "",
  additional_info: "",
  keywords: "",
  tags: "",

  base_price: "",
  discount_type: "none",
  discount_value: "",
  final_price: "",

  stock_status: "in_stock",
  status: "draft",
  is_printable: false,
  requires_order_questionnaire: false,

  existingMedia: [],
  delete_image_ids: [],
  primary_image_id: "",

  template_ids: [],

  /*
   * Structure:
   *
   * {
   *   [value_id]: {
   *     value_id,
   *     price_adjustment,
   *     is_available
   *   }
   * }
   */
  option_value_overrides: {},
};

const STEPS = [
  {
    key: "basics",
    title: "Basics",
    hint: "Name, SKU & category",
    icon: FiPackage,
    description:
      "Start with what this product is called and where it sits in your catalogue.",
  },
  {
    key: "media",
    title: "Photos & videos",
    hint: "What shoppers see first",
    icon: FiImage,
    description:
      "Add photos and videos. The cover image is the one shown in product listings.",
  },
  {
    key: "pricing",
    title: "Pricing & tax",
    hint: "Price, discount & GST",
    icon: FiTag,
    description:
      "Set the price customers pay. The final price updates as you type.",
  },
  {
    key: "options",
    title: "Options",
    hint: "Paper, size, finish…",
    icon: FiLayers,
    description:
      "Attach option templates so customers can personalise their order. This step is optional.",
  },
  {
    key: "details",
    title: "Description",
    hint: "Story, details & search",
    icon: FiFileText,
    description:
      "Describe the product and add keywords so shoppers can find it.",
  },
  {
    key: "review",
    title: "Review & publish",
    hint: "Visibility & final check",
    icon: FiSend,
    description:
      "Check everything, choose who can see this product, then save.",
  },
];

/*
 * Which step each validated field lives on, so a
 * failed save can jump straight to the problem.
 */
const FIELD_STEP = {
  product_name: "basics",
  sku: "basics",
  category_id: "basics",
  tax_class_id: "pricing",
  base_price: "pricing",
  discount_value: "pricing",
};

const STATUS_CHOICES = [
  {
    value: "draft",
    title: "Draft",
    description:
      "Hidden from the store. Keep working on it later.",
  },
  {
    value: "active",
    title: "Active",
    description:
      "Live in the store and available to order.",
  },
  {
    value: "inactive",
    title: "Inactive",
    description:
      "Hidden from the store, kept for records.",
  },
];

const PERCENT_SHORTCUTS = [5, 10, 15, 20, 25];

const PAGE_NAME = "Products";

const getPermissionHeaders = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const showRequestError = (
  error,
  fallbackMessage
) => {
  if (error.response?.status === 403) {
    return;
  }

  if (error.code === "ECONNABORTED") {
    toast.error(
      "The server is taking too long to respond. Check the product list before trying again."
    );
    return;
  }

  if (!error.response) {
    toast.error(
      "Could not reach the server. Check your connection and try again."
    );
    return;
  }

  toast.error(
    error.response?.data?.message ||
      fallbackMessage
  );
};

/*
 * Saving uploads and compresses media, which can
 * take well over the default 15s API timeout.
 */
const PRODUCT_SAVE_TIMEOUT = 10 * 60 * 1000;

const formatPrice = (value) =>
  `₹${Number(value || 0).toLocaleString(
    "en-IN",
    {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }
  )}`;

const splitTags = (value) =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const createSkuFromName = (
  name,
  suffix
) => {
  const parts = String(name || "")
    .toUpperCase()
    .replace(/[^A-Z0-9 ]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map((word) => word.slice(0, 3));

  return parts.length
    ? [...parts, suffix].join("-")
    : "";
};

const createSkuSuffix = () =>
  String(
    Math.floor(1000 + Math.random() * 9000)
  );

const validateForm = (form) => {
  const errors = {};

  if (!String(form.product_name).trim()) {
    errors.product_name =
      "Give the product a name.";
  }

  if (!String(form.sku).trim()) {
    errors.sku =
      "SKU is required. Use Generate if you don't have one.";
  }

  if (!form.category_id) {
    errors.category_id =
      "Choose a category.";
  }

  if (!form.tax_class_id) {
    errors.tax_class_id =
      "Choose the HSN/SAC tax class.";
  }

  if (
    form.base_price === "" ||
    form.base_price === null ||
    form.base_price === undefined
  ) {
    errors.base_price =
      "Enter the base price.";
  }

  const basePrice = Number(
    form.base_price || 0
  );

  const discountValue = Number(
    form.discount_value || 0
  );

  if (
    form.discount_type === "percentage" &&
    discountValue > 100
  ) {
    errors.discount_value =
      "A percentage discount can't be more than 100%.";
  }

  if (
    form.discount_type === "flat" &&
    discountValue > basePrice
  ) {
    errors.discount_value =
      "A flat discount can't be more than the base price.";
  }

  return errors;
};

const isVideoMedia = (media) => {
  const mediaType = String(
    media?.media_type ||
      media?.type ||
      ""
  ).toLowerCase();

  const url = String(
    media?.image_url ||
      media?.preview ||
      media?.url ||
      ""
  ).toLowerCase();

  const name = String(
    media?.name ||
      media?.originalname ||
      ""
  ).toLowerCase();

  return (
    mediaType === "video" ||
    mediaType.startsWith("video/") ||
    url.endsWith(".mp4") ||
    url.endsWith(".mov") ||
    url.endsWith(".webm") ||
    name.endsWith(".mp4") ||
    name.endsWith(".mov") ||
    name.endsWith(".webm")
  );
};

const waitForImages = async (
  mediaItems = []
) => {
  const urls = mediaItems
    .filter(
      (item) => !isVideoMedia(item)
    )
    .map((item) => item.image_url)
    .filter(Boolean);

  await Promise.all(
    urls.map(
      (url) =>
        new Promise((resolve) => {
          const image = new Image();

          image.onload = resolve;
          image.onerror = resolve;
          image.src = url;
        })
    )
  );
};

const toBoolean = (
  value,
  fallback = true
) => {
  if (
    value === undefined ||
    value === null
  ) {
    return fallback;
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value === 1;
  }

  const normalized = String(value)
    .trim()
    .toLowerCase();

  if (
    ["true", "1", "yes"].includes(
      normalized
    )
  ) {
    return true;
  }

  if (
    ["false", "0", "no"].includes(
      normalized
    )
  ) {
    return false;
  }

  return fallback;
};

const getTemplateValueIds = (
  template
) => {
  const valueIds = [];

  for (const field of
    template?.fields || []) {
    for (const value of
      field?.values || []) {
      if (value?.value_id) {
        valueIds.push(
          String(value.value_id)
        );
      }
    }
  }

  return valueIds;
};

const getTemplateDefaultPrice = (value) =>
  value.template_price_adjustment ??
  value.price_adjustment ??
  0;

/*
 * Creates editable override values from a template.
 *
 * For a new product, template defaults are copied into
 * product-specific overrides.
 */
const createOverridesFromTemplate = (
  template,
  existingOverrides = {}
) => {
  const nextOverrides = {
    ...existingOverrides,
  };

  for (const field of
    template?.fields || []) {
    for (const value of
      field?.values || []) {
      const valueId = String(
        value.value_id
      );

      if (!valueId) {
        continue;
      }

      if (!nextOverrides[valueId]) {
        nextOverrides[valueId] = {
          value_id: Number(
            value.value_id
          ),

          price_adjustment: String(
            getTemplateDefaultPrice(value)
          ),

          is_available:
            value.is_available ===
            undefined
              ? true
              : toBoolean(
                  value.is_available,
                  true
                ),
        };
      }
    }
  }

  return nextOverrides;
};

/*
 * Builds override state while editing a product.
 *
 * It supports both:
 * 1. product.option_value_overrides
 * 2. merged values inside option_templates
 */
const buildProductOverrides = (
  product
) => {
  const overrideMap = {};

  /*
   * First read raw database overrides.
   */
  for (const override of
    product?.option_value_overrides || []) {
    const valueId = String(
      override.value_id
    );

    overrideMap[valueId] = {
      value_id: Number(
        override.value_id
      ),

      price_adjustment: String(
        override.price_adjustment ?? 0
      ),

      is_available: toBoolean(
        override.is_available,
        true
      ),
    };
  }

  /*
   * Then read the values returned inside
   * product.option_templates.
   *
   * This also supports the backend response where
   * product-specific overrides have already been
   * merged into each option value.
   */
  for (const template of
    product?.option_templates || []) {
    for (const field of
      template?.fields || []) {
      for (const value of
        field?.values || []) {
        const valueId = String(
          value.value_id
        );

        if (!valueId) {
          continue;
        }

        if (
          value.has_override ||
          value.override_id ||
          !overrideMap[valueId]
        ) {
          overrideMap[valueId] = {
            value_id: Number(
              value.value_id
            ),

            price_adjustment: String(
              value.price_adjustment ??
                value.template_price_adjustment ??
                0
            ),

            is_available:
              value.is_available ===
              undefined
                ? true
                : toBoolean(
                    value.is_available,
                    true
                  ),
          };
        }
      }
    }
  }

  return overrideMap;
};

/* ---------- Small building blocks ---------- */

const Field = ({
  id,
  label,
  required = false,
  hint,
  error,
  className = "",
  children,
}) => (
  <div
    className={`pf-field ${
      error ? "has-error" : ""
    } ${className}`}
  >
    {label && (
      <label
        className="pf-label"
        htmlFor={id}
      >
        {label}

        {required ? (
          <span className="pf-required">
            *
          </span>
        ) : (
          <span className="pf-optional">
            Optional
          </span>
        )}
      </label>
    )}

    {children}

    {error ? (
      <p
        className="pf-error"
        role="alert"
      >
        <FiAlertCircle /> {error}
      </p>
    ) : (
      hint && (
        <p className="pf-hint">{hint}</p>
      )
    )}
  </div>
);

const Segmented = ({
  value,
  options,
  onChange,
  ariaLabel,
}) => (
  <div
    className="pf-segmented"
    role="radiogroup"
    aria-label={ariaLabel}
  >
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        role="radio"
        aria-checked={
          value === option.value
        }
        className={
          value === option.value
            ? "is-active"
            : ""
        }
        onClick={() =>
          onChange(option.value)
        }
      >
        {option.label}
      </button>
    ))}
  </div>
);

const SwitchRow = ({
  name,
  checked,
  onChange,
  title,
  description,
}) => (
  <label className="pf-switch-row">
    <span className="pf-switch-text">
      <strong>{title}</strong>
      <small>{description}</small>
    </span>

    <input
      type="checkbox"
      role="switch"
      className="pf-switch-input"
      name={name}
      checked={checked}
      onChange={onChange}
    />

    <span
      className="pf-switch"
      aria-hidden="true"
    />
  </label>
);

const TagInput = ({
  id,
  value,
  onChange,
  placeholder,
}) => {
  const [draft, setDraft] =
    useState("");

  const inputRef = useRef(null);

  const tags = splitTags(value);

  const commit = (raw) => {
    const known = new Set(
      tags.map((tag) =>
        tag.toLowerCase()
      )
    );

    const additions = [];

    splitTags(raw).forEach((tag) => {
      if (!known.has(tag.toLowerCase())) {
        known.add(tag.toLowerCase());
        additions.push(tag);
      }
    });

    if (additions.length) {
      onChange(
        [...tags, ...additions].join(", ")
      );
    }

    setDraft("");
  };

  const removeTag = (index) => {
    onChange(
      tags
        .filter((_, i) => i !== index)
        .join(", ")
    );
  };

  return (
    <div
      className="pf-tags"
      onClick={() =>
        inputRef.current?.focus()
      }
    >
      {tags.map((tag, index) => (
        <span
          key={`${tag}-${index}`}
          className="pf-tag"
        >
          {tag}

          <button
            type="button"
            aria-label={`Remove ${tag}`}
            onClick={(event) => {
              event.stopPropagation();
              removeTag(index);
            }}
          >
            <FiX />
          </button>
        </span>
      ))}

      <input
        id={id}
        ref={inputRef}
        value={draft}
        placeholder={
          tags.length ? "Add more…" : placeholder
        }
        onChange={(event) => {
          const nextValue =
            event.target.value;

          if (nextValue.includes(",")) {
            commit(nextValue);
          } else {
            setDraft(nextValue);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            commit(draft);
          } else if (
            event.key === "Backspace" &&
            !draft &&
            tags.length
          ) {
            removeTag(tags.length - 1);
          }
        }}
        onBlur={() => {
          if (draft.trim()) {
            commit(draft);
          }
        }}
      />
    </div>
  );
};

const ProductForm = () => {
  const navigate = useNavigate();

  const { product_id } =
    useParams();

  const isEdit =
    Boolean(product_id);

  const [form, setForm] =
    useState(initialForm);

  const [
    categories,
    setCategories,
  ] = useState([]);

  const [
    subCategories,
    setSubCategories,
  ] = useState([]);

  const [
    optionTemplates,
    setOptionTemplates,
  ] = useState([]);

  const [taxClasses, setTaxClasses] =
    useState([]);

  /*
   * Newly picked files:
   * [{ id, file, url, isVideo }]
   */
  const [newMedia, setNewMedia] =
    useState([]);

  /*
   * Saved media the admin removed in this
   * session, kept so the removal can be undone.
   */
  const [removedMedia, setRemovedMedia] =
    useState([]);

  const [saving, setSaving] =
    useState(false);

  /*
   * Blocks a second submit before the saving
   * state has re-rendered the buttons.
   */
  const savingRef = useRef(false);

  const [
    pageLoading,
    setPageLoading,
  ] = useState(isEdit);

  const [step, setStep] = useState(0);

  const [visitedSteps, setVisitedSteps] =
    useState(() => new Set([0]));

  const [
    attemptedSteps,
    setAttemptedSteps,
  ] = useState(() => new Set());

  const [
    submitAttempted,
    setSubmitAttempted,
  ] = useState(false);

  const [touched, setTouched] =
    useState({});

  const [serverErrors, setServerErrors] =
    useState({});

  const [skuTouched, setSkuTouched] =
    useState(isEdit);

  const [skuSuffix, setSkuSuffix] =
    useState(createSkuSuffix);

  const [dragActive, setDragActive] =
    useState(false);

  const [
    expandedTemplates,
    setExpandedTemplates,
  ] = useState(() => new Set());

  const [advancedOpen, setAdvancedOpen] =
    useState(false);

  const stepTopRef = useRef(null);
  const fileInputRef = useRef(null);
  const newMediaRef = useRef(newMedia);

  const filteredSubCategories =
    useMemo(() => {
      if (!form.category_id) {
        return subCategories;
      }

      return subCategories.filter(
        (item) =>
          String(item.category_id) ===
          String(form.category_id)
      );
    }, [
      form.category_id,
      subCategories,
    ]);

  const selectedTemplates =
    useMemo(() => {
      const selectedIds = new Set(
        form.template_ids.map(String)
      );

      return optionTemplates.filter(
        (template) =>
          selectedIds.has(
            String(
              template.template_id
            )
          )
      );
    }, [
      optionTemplates,
      form.template_ids,
    ]);

  const calculatedFinalPrice =
    useMemo(() => {
      const basePrice = Math.max(
        0,
        Number(
          form.base_price || 0
        )
      );

      const discountValue =
        Math.max(
          0,
          Number(
            form.discount_value || 0
          )
        );

      if (
        form.discount_type === "flat"
      ) {
        return Math.max(
          basePrice -
            Math.min(
              discountValue,
              basePrice
            ),
          0
        );
      }

      if (
        form.discount_type ===
        "percentage"
      ) {
        const percentage =
          Math.min(
            discountValue,
            100
          );

        return Math.max(
          basePrice -
            (basePrice *
              percentage) /
              100,
          0
        );
      }

      return basePrice;
    }, [
      form.base_price,
      form.discount_type,
      form.discount_value,
    ]);

  const errors = useMemo(
    () => ({
      ...validateForm(form),
      ...serverErrors,
    }),
    [form, serverErrors]
  );

  const fetchMeta = async () => {
    const [
      categoryResponse,
      subCategoryResponse,
      templateResponse,
      taxClassResponse,
    ] = await Promise.all([
      api.get(
        "/admin/products/categories",
        {
          headers:
            getPermissionHeaders(
              "view"
            ),
        }
      ),

      api.get(
        "/admin/products/sub-categories",
        {
          headers:
            getPermissionHeaders(
              "view"
            ),
        }
      ),

      api.get(
        "/admin/products/option-templates",
        {
          headers:
            getPermissionHeaders(
              "view"
            ),
        }
      ),

      api.get(
        "/admin/products/tax-classes?status=active",
        {
          headers:
            getPermissionHeaders(
              "view"
            ),
        }
      ),
    ]);

    const loadedTemplates =
      templateResponse.data?.data ||
      [];

    setCategories(
      categoryResponse.data?.data || []
    );

    setSubCategories(
      subCategoryResponse.data?.data || []
    );

    setOptionTemplates(
      loadedTemplates
    );

    setTaxClasses(
      taxClassResponse.data?.data || []
    );

    return {
      optionTemplates:
        loadedTemplates,
    };
  };

  const fetchProduct = async (
    loadedTemplates = []
  ) => {
    if (!isEdit) {
      return;
    }

    const response =
      await api.get(
        `/admin/products/products/${product_id}`,
        {
          headers:
            getPermissionHeaders(
              "view"
            ),
        }
      );

    const product =
      response.data?.data;

    if (!product) {
      toast.error(
        "Product not found."
      );

      navigate("/products", {
        replace: true,
      });

      return;
    }

    await waitForImages(
      product.media || []
    );

    const selectedTemplateIds =
      (
        product.option_templates ||
        []
      ).map((template) =>
        String(
          template.template_id
        )
      );

    const existingMedia =
      product.media || [];

    /*
     * Start with the product's saved overrides.
     */
    let productOverrides =
      buildProductOverrides(product);

    /*
     * Ensure all selected template values are
     * available in override state.
     *
     * Product templates are preferred because
     * they may contain the merged effective
     * product-specific values.
     */
    for (const template of
      product.option_templates ||
      []) {
      productOverrides =
        createOverridesFromTemplate(
          template,
          productOverrides
        );
    }

    /*
     * Fallback to meta templates when necessary.
     */
    for (const templateId of
      selectedTemplateIds) {
      const template =
        loadedTemplates.find(
          (item) =>
            String(
              item.template_id
            ) ===
            String(templateId)
        );

      if (template) {
        productOverrides =
          createOverridesFromTemplate(
            template,
            productOverrides
          );
      }
    }

    setForm({
      category_id:
        product.category_id || "",

      sub_category_id:
        product.sub_category_id ||
        "",

      tax_class_id:
        product.tax_class_id || "",

      product_name:
        product.product_name || "",

      sku: product.sku || "",

      short_description:
        product.short_description ||
        "",

      description:
        product.description || "",

      product_info:
        product.product_info || "",

      additional_info:
        product.additional_info ||
        "",

      keywords:
        product.keywords || "",

      tags:
        product.tags || "",

      base_price:
        product.base_price ?? "",

      discount_type:
        product.discount_type ||
        "none",

      discount_value:
        product.discount_value ??
        "",

      final_price:
        product.final_price ?? "",

      stock_status:
        product.stock_status ||
        "in_stock",

      status:
        product.status || "draft",

      is_printable: Boolean(
        product.is_printable
      ),

      requires_order_questionnaire: Boolean(
        product.requires_order_questionnaire
      ),

      existingMedia,

      delete_image_ids: [],

      primary_image_id:
        existingMedia.find(
          (media) =>
            media.is_primary &&
            !isVideoMedia(media)
        )?.image_id || "",

      template_ids:
        selectedTemplateIds,

      option_value_overrides:
        productOverrides,
    });

    setAdvancedOpen(
      Boolean(
        product.product_info ||
          product.additional_info
      )
    );

    /*
     * Every step of a saved product can be
     * jumped to straight away.
     */
    setVisitedSteps(
      new Set(STEPS.map((_, index) => index))
    );
  };

  useEffect(() => {
    let active = true;

    const loadPage = async () => {
      try {
        setPageLoading(true);

        const metadata =
          await fetchMeta();

        if (
          isEdit &&
          active
        ) {
          await fetchProduct(
            metadata.optionTemplates
          );
        }
      } catch (error) {
        showRequestError(
          error,
          isEdit
            ? "Failed to load product details."
            : "Failed to load product form data."
        );

        if (
          isEdit &&
          error.response?.status !==
            403
        ) {
          navigate("/products", {
            replace: true,
          });
        }
      } finally {
        if (active) {
          setPageLoading(false);
        }
      }
    };

    loadPage();

    return () => {
      active = false;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product_id]);

  useEffect(() => {
    setForm((previous) => {
      const nextFinalPrice =
        calculatedFinalPrice.toFixed(
          2
        );

      if (
        String(
          previous.final_price
        ) === nextFinalPrice
      ) {
        return previous;
      }

      return {
        ...previous,
        final_price:
          nextFinalPrice,
      };
    });
  }, [calculatedFinalPrice]);

  useEffect(() => {
    newMediaRef.current = newMedia;
  }, [newMedia]);

  /*
   * Release preview URLs when leaving the page.
   */
  useEffect(
    () => () => {
      newMediaRef.current.forEach(
        (item) =>
          URL.revokeObjectURL(item.url)
      );
    },
    []
  );

  /* ---------- Field handling ---------- */

  const handleChange = (
    event
  ) => {
    const {
      name,
      value,
      checked,
      type,
    } = event.target;

    let nextValue =
      type === "checkbox"
        ? checked
        : value;

    if (
      name === "base_price" ||
      name === "discount_value"
    ) {
      nextValue =
        value === ""
          ? ""
          : Math.max(
              0,
              Number(value)
            );
    }

    if (name === "sku") {
      setSkuTouched(true);
    }

    if (serverErrors[name]) {
      setServerErrors((previous) => {
        const next = { ...previous };
        delete next[name];
        return next;
      });
    }

    setForm((previous) => {
      const updatedForm = {
        ...previous,
        [name]: nextValue,
      };

      if (
        name === "category_id"
      ) {
        updatedForm.sub_category_id =
          "";
      }

      if (
        name ===
          "discount_type" &&
        value === "none"
      ) {
        updatedForm.discount_value =
          "";
      }

      if (
        name === "product_name" &&
        !skuTouched
      ) {
        updatedForm.sku =
          createSkuFromName(
            value,
            skuSuffix
          );
      }

      return updatedForm;
    });
  };

  const setField = (name, value) =>
    handleChange({
      target: {
        name,
        value,
        type: "text",
      },
    });

  const touch = (name) =>
    setTouched((previous) =>
      previous[name]
        ? previous
        : { ...previous, [name]: true }
    );

  const regenerateSku = () => {
    const suffix = createSkuSuffix();

    setSkuSuffix(suffix);
    setSkuTouched(false);

    setServerErrors((previous) => {
      const next = { ...previous };
      delete next.sku;
      return next;
    });

    setForm((previous) => ({
      ...previous,
      sku:
        createSkuFromName(
          previous.product_name,
          suffix
        ) || previous.sku,
    }));
  };

  const getFieldError = (name) => {
    if (serverErrors[name]) {
      return serverErrors[name];
    }

    const visible =
      submitAttempted ||
      touched[name] ||
      attemptedSteps.has(
        FIELD_STEP[name]
      );

    return visible
      ? errors[name]
      : null;
  };

  const getStepErrorFields = (stepKey) =>
    Object.keys(errors).filter(
      (field) =>
        FIELD_STEP[field] === stepKey
    );

  /* ---------- Step navigation ---------- */

  const goToStep = (index) => {
    setStep(index);

    setVisitedSteps((previous) => {
      const next = new Set(previous);
      next.add(index);
      return next;
    });

    requestAnimationFrame(() => {
      stepTopRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const goNext = () => {
    const stepKey = STEPS[step].key;

    if (
      getStepErrorFields(stepKey).length
    ) {
      setAttemptedSteps((previous) => {
        const next = new Set(previous);
        next.add(stepKey);
        return next;
      });

      toast.error(
        "Please complete the highlighted fields to continue."
      );

      return;
    }

    goToStep(
      Math.min(step + 1, STEPS.length - 1)
    );
  };

  const goBack = () =>
    goToStep(Math.max(step - 1, 0));

  const getStepState = (index) => {
    const stepKey = STEPS[index].key;

    const hasErrors =
      getStepErrorFields(stepKey).length >
      0;

    if (index === step) {
      return "current";
    }

    if (
      hasErrors &&
      (submitAttempted ||
        attemptedSteps.has(stepKey))
    ) {
      return "error";
    }

    if (
      visitedSteps.has(index) &&
      !hasErrors
    ) {
      return "done";
    }

    return "todo";
  };

  /* ---------- Media ---------- */

  const hasExistingImages =
    form.existingMedia.some(
      (media) => !isVideoMedia(media)
    );

  const existingCoverId = useMemo(() => {
    const images =
      form.existingMedia.filter(
        (media) => !isVideoMedia(media)
      );

    const cover =
      images.find((media) =>
        form.primary_image_id
          ? String(media.image_id) ===
            String(form.primary_image_id)
          : media.is_primary
      ) || images[0];

    return cover?.image_id ?? null;
  }, [
    form.existingMedia,
    form.primary_image_id,
  ]);

  /*
   * With no saved images, the first new image
   * becomes the cover when the product is saved.
   */
  const newCoverId = hasExistingImages
    ? null
    : newMedia.find(
        (item) => !item.isVideo
      )?.id ?? null;

  const coverUrl =
    form.existingMedia.find(
      (media) =>
        media.image_id === existingCoverId
    )?.image_url ||
    newMedia.find(
      (item) => item.id === newCoverId
    )?.url ||
    "";

  const hasAnyImage =
    hasExistingImages ||
    newMedia.some(
      (item) => !item.isVideo
    );

  const totalMediaCount =
    form.existingMedia.length +
    newMedia.length;

  const addFiles = (fileList) => {
    const acceptedTypes =
      MEDIA_ACCEPT.split(",");

    const files = Array.from(
      fileList || []
    );

    const validFiles = files.filter(
      (file) =>
        acceptedTypes.includes(file.type) ||
        MEDIA_EXTENSION_PATTERN.test(
          file.name
        )
    );

    const skipped =
      files.length - validFiles.length;

    if (skipped > 0) {
      toast.error(
        `${skipped} file${
          skipped > 1 ? "s were" : " was"
        } skipped. Use JPG, PNG, WEBP, MP4, MOV or WEBM.`
      );
    }

    if (!validFiles.length) {
      return;
    }

    setNewMedia((previous) => [
      ...previous,
      ...validFiles.map((file) => ({
        id: `${file.name}-${file.size}-${
          file.lastModified
        }-${Math.random()
          .toString(36)
          .slice(2, 8)}`,
        file,
        url: URL.createObjectURL(file),
        isVideo:
          file.type.startsWith("video/") ||
          /\.(mp4|mov|webm)$/i.test(
            file.name
          ),
      })),
    ]);
  };

  const handleFileInput = (event) => {
    addFiles(event.target.files);

    /*
     * Allow picking the same file again after
     * removing it.
     */
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    addFiles(event.dataTransfer.files);
  };

  const removeNewMedia = (id) => {
    setNewMedia((previous) => {
      const target = previous.find(
        (item) => item.id === id
      );

      if (target) {
        URL.revokeObjectURL(target.url);
      }

      return previous.filter(
        (item) => item.id !== id
      );
    });
  };

  const makeNewMediaCover = (id) => {
    setNewMedia((previous) => {
      const target = previous.find(
        (item) => item.id === id
      );

      return target
        ? [
            target,
            ...previous.filter(
              (item) => item.id !== id
            ),
          ]
        : previous;
    });
  };

  const markMediaForDelete = (
    media
  ) => {
    const imageId = media.image_id;

    setRemovedMedia((previous) => [
      ...previous,
      media,
    ]);

    setForm((previous) => ({
      ...previous,

      delete_image_ids:
        Array.from(
          new Set([
            ...previous.delete_image_ids,
            imageId,
          ])
        ),

      primary_image_id:
        String(
          previous.primary_image_id
        ) === String(imageId)
          ? ""
          : previous.primary_image_id,

      existingMedia:
        previous.existingMedia.filter(
          (item) =>
            Number(
              item.image_id
            ) !==
            Number(imageId)
        ),
    }));
  };

  const restoreMedia = (media) => {
    setRemovedMedia((previous) =>
      previous.filter(
        (item) =>
          item.image_id !== media.image_id
      )
    );

    setForm((previous) => ({
      ...previous,

      delete_image_ids:
        previous.delete_image_ids.filter(
          (id) =>
            Number(id) !==
            Number(media.image_id)
        ),

      existingMedia: [
        ...previous.existingMedia,
        {
          ...media,
          is_primary:
            previous.primary_image_id
              ? String(
                  previous.primary_image_id
                ) ===
                String(media.image_id)
              : media.is_primary,
        },
      ].sort(
        (a, b) =>
          Number(a.sort_order || 0) -
          Number(b.sort_order || 0)
      ),
    }));
  };

  const setPrimaryImage = (
    imageId
  ) => {
    setForm((previous) => ({
      ...previous,

      primary_image_id:
        imageId,

      existingMedia:
        previous.existingMedia.map(
          (media) => ({
            ...media,

            is_primary:
              Number(
                media.image_id
              ) ===
              Number(imageId),
          })
        ),
    }));
  };

  /* ---------- Option templates ---------- */

  const toggleTemplate = (
    templateId
  ) => {
    const normalizedTemplateId =
      String(templateId);

    const template =
      optionTemplates.find(
        (item) =>
          String(
            item.template_id
          ) ===
          normalizedTemplateId
      );

    setForm((previous) => {
      const exists =
        previous.template_ids.includes(
          normalizedTemplateId
        );

      if (exists) {
        const valueIds =
          getTemplateValueIds(
            template
          );

        const nextOverrides = {
          ...previous.option_value_overrides,
        };

        /*
         * Remove override values belonging to
         * the removed template.
         */
        valueIds.forEach(
          (valueId) => {
            delete nextOverrides[
              valueId
            ];
          }
        );

        return {
          ...previous,

          template_ids:
            previous.template_ids.filter(
              (item) =>
                item !==
                normalizedTemplateId
            ),

          option_value_overrides:
            nextOverrides,
        };
      }

      return {
        ...previous,

        template_ids: [
          ...previous.template_ids,
          normalizedTemplateId,
        ],

        /*
         * Create product-specific values from
         * the template defaults.
         */
        option_value_overrides:
          createOverridesFromTemplate(
            template,
            previous.option_value_overrides
          ),
      };
    });
  };

  const toggleTemplateExpanded = (
    templateId
  ) => {
    setExpandedTemplates((previous) => {
      const next = new Set(previous);
      const key = String(templateId);

      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      return next;
    });
  };

  const updateOverride = (
    valueId,
    changes,
    defaultPrice = 0
  ) => {
    const normalizedValueId =
      String(valueId);

    setForm((previous) => {
      const current =
        previous
          .option_value_overrides[
          normalizedValueId
        ] || {
          value_id:
            Number(valueId),

          price_adjustment:
            String(
              defaultPrice ?? 0
            ),

          is_available: true,
        };

      return {
        ...previous,

        option_value_overrides: {
          ...previous.option_value_overrides,

          [normalizedValueId]: {
            ...current,
            ...changes,

            value_id:
              Number(valueId),
          },
        },
      };
    });
  };

  const resetTemplateOverrides = (
    template
  ) => {
    setForm((previous) => {
      const nextOverrides = {
        ...previous.option_value_overrides,
      };

      getTemplateValueIds(
        template
      ).forEach((valueId) => {
        delete nextOverrides[valueId];
      });

      return {
        ...previous,

        option_value_overrides:
          createOverridesFromTemplate(
            template,
            nextOverrides
          ),
      };
    });
  };

  const getOverrideForValue = (
    value
  ) => {
    const valueId = String(
      value.value_id
    );

    return (
      form.option_value_overrides[
        valueId
      ] || {
        value_id: Number(
          value.value_id
        ),

        price_adjustment: String(
          getTemplateDefaultPrice(value)
        ),

        is_available:
          value.is_available ===
          undefined
            ? true
            : toBoolean(
                value.is_available,
                true
              ),
      }
    );
  };

  const getTemplateStats = (template) => {
    let total = 0;
    let customised = 0;

    for (const field of
      template?.fields || []) {
      for (const value of
        field?.values || []) {
        total += 1;

        const override =
          getOverrideForValue(value);

        if (
          Number(
            override.price_adjustment || 0
          ) !==
            Number(
              getTemplateDefaultPrice(value)
            ) ||
          override.is_available === false
        ) {
          customised += 1;
        }
      }
    }

    return { total, customised };
  };

  const getOverridesForSubmit =
    () => {
      const selectedTemplateIdSet =
        new Set(
          form.template_ids.map(
            String
          )
        );

      const selectedValueIds =
        new Set();

      optionTemplates.forEach(
        (template) => {
          if (
            !selectedTemplateIdSet.has(
              String(
                template.template_id
              )
            )
          ) {
            return;
          }

          getTemplateValueIds(
            template
          ).forEach((valueId) => {
            selectedValueIds.add(
              String(valueId)
            );
          });
        }
      );

      return Array.from(
        selectedValueIds
      )
        .map((valueId) => {
          const override =
            form
              .option_value_overrides[
              valueId
            ];

          if (!override) {
            return null;
          }

          const priceAdjustment =
            Number(
              override.price_adjustment ||
                0
            );

          return {
            value_id: Number(
              override.value_id ||
                valueId
            ),

            price_adjustment:
              Number.isFinite(
                priceAdjustment
              )
                ? priceAdjustment
                : 0,

            is_available:
              override.is_available !==
              false,
          };
        })
        .filter(Boolean);
    };

  /* ---------- Save ---------- */

  const submitProduct = async () => {
    if (savingRef.current) {
      return;
    }

    setSubmitAttempted(true);

    const firstErrorField =
      Object.keys(errors)[0];

    if (firstErrorField) {
      goToStep(
        STEPS.findIndex(
          (item) =>
            item.key ===
            FIELD_STEP[firstErrorField]
        )
      );

      toast.error(
        "A few required details are missing. They're highlighted for you."
      );

      return;
    }

    const basePrice = Number(
      form.base_price || 0
    );

    const discountValue =
      Number(
        form.discount_value || 0
      );

    /*
     * The backend makes the first uploaded image
     * the cover, so send the chosen cover first.
     */
    const orderedMedia = [...newMedia];

    if (!hasExistingImages) {
      const coverIndex =
        orderedMedia.findIndex(
          (item) => !item.isVideo
        );

      if (coverIndex > 0) {
        orderedMedia.unshift(
          orderedMedia.splice(
            coverIndex,
            1
          )[0]
        );
      }
    }

    try {
      savingRef.current = true;
      setSaving(true);

      const formData =
        new FormData();

      formData.append(
        "category_id",
        form.category_id
      );

      formData.append(
        "sub_category_id",
        form.sub_category_id || ""
      );

      formData.append(
        "tax_class_id",
        form.tax_class_id
      );

      formData.append(
        "product_name",
        form.product_name.trim()
      );

      formData.append(
        "sku",
        form.sku.trim()
      );

      formData.append(
        "short_description",
        form.short_description.trim()
      );

      formData.append(
        "description",
        form.description || ""
      );

      formData.append(
        "product_info",
        form.product_info || ""
      );

      formData.append(
        "additional_info",
        form.additional_info || ""
      );

      formData.append(
        "keywords",
        form.keywords.trim()
      );

      formData.append(
        "tags",
        form.tags.trim()
      );

      formData.append(
        "base_price",
        String(basePrice)
      );

      formData.append(
        "discount_type",
        form.discount_type ||
          "none"
      );

      formData.append(
        "discount_value",
        form.discount_type ===
          "none"
          ? "0"
          : String(
              discountValue
            )
      );

      formData.append(
        "final_price",
        String(
          calculatedFinalPrice
        )
      );

      formData.append(
        "stock_status",
        form.stock_status
      );

      formData.append(
        "status",
        form.status
      );

      formData.append(
        "is_printable",
        form.is_printable
          ? "true"
          : "false"
      );

      formData.append(
        "requires_order_questionnaire",
        form.requires_order_questionnaire
          ? "true"
          : "false"
      );

      formData.append(
        "template_ids",
        JSON.stringify(
          form.template_ids || []
        )
      );

      formData.append(
        "option_value_overrides",
        JSON.stringify(
          getOverridesForSubmit()
        )
      );

      formData.append(
        "delete_image_ids",
        JSON.stringify(
          form.delete_image_ids ||
            []
        )
      );

      formData.append(
        "primary_image_id",
        form.primary_image_id ||
          ""
      );

      orderedMedia.forEach(
        (item) => {
          formData.append(
            "media",
            item.file
          );
        }
      );

      if (isEdit) {
        await api.put(
          `/admin/products/products/${product_id}`,
          formData,
          {
            headers:
              getPermissionHeaders(
                "edit"
              ),
            timeout:
              PRODUCT_SAVE_TIMEOUT,
          }
        );

        toast.success(
          "Product updated successfully."
        );
      } else {
        await api.post(
          "/admin/products/products",
          formData,
          {
            headers:
              getPermissionHeaders(
                "add"
              ),
            timeout:
              PRODUCT_SAVE_TIMEOUT,
          }
        );

        toast.success(
          form.status === "active"
            ? "Product published successfully."
            : "Product created successfully."
        );
      }

      navigate("/products", {
        replace: true,
      });
    } catch (error) {
      if (error.response?.status === 409) {
        setServerErrors({
          sku:
            error.response?.data?.message ||
            "This SKU is already used by another product.",
        });

        goToStep(0);

        toast.error(
          error.response?.data?.message ||
            "That SKU is already taken. Please choose another."
        );
      } else {
        showRequestError(
          error,
          "Product save failed."
        );
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();

    if (step === STEPS.length - 1) {
      submitProduct();
    } else {
      goNext();
    }
  };

  /*
   * Stops Enter in a text box from jumping steps
   * or saving before the admin is ready.
   */
  const handleFormKeyDown = (event) => {
    if (
      event.key === "Enter" &&
      event.target.tagName === "INPUT"
    ) {
      event.preventDefault();
    }
  };

  /* ---------- Derived display values ---------- */

  const selectedCategory =
    categories.find(
      (item) =>
        String(item.category_id) ===
        String(form.category_id)
    );

  const selectedSubCategory =
    subCategories.find(
      (item) =>
        String(item.sub_category_id) ===
        String(form.sub_category_id)
    );

  const selectedTaxClass =
    taxClasses.find(
      (item) =>
        String(item.tax_class_id) ===
        String(form.tax_class_id)
    );

  const basePriceNumber = Number(
    form.base_price || 0
  );

  const savings = Math.max(
    basePriceNumber - calculatedFinalPrice,
    0
  );

  const savingsPercent =
    basePriceNumber > 0
      ? Math.round(
          (savings / basePriceNumber) * 100
        )
      : 0;

  const checklist = [
    {
      label: "Name, SKU & category",
      done:
        !errors.product_name &&
        !errors.sku &&
        !errors.category_id,
      required: true,
      step: 0,
    },
    {
      label: "At least one photo",
      done: hasAnyImage,
      required: false,
      step: 1,
    },
    {
      label: "Price & tax class",
      done:
        !errors.base_price &&
        !errors.tax_class_id &&
        !errors.discount_value,
      required: true,
      step: 2,
    },
    {
      label: "Short description",
      done: Boolean(
        form.short_description.trim()
      ),
      required: false,
      step: 0,
    },
    {
      label: "Full description",
      done: Boolean(
        form.description.trim()
      ),
      required: false,
      step: 4,
    },
  ];

  const checklistDone =
    checklist.filter((item) => item.done)
      .length;

  const progressPercent = Math.round(
    (checklistDone / checklist.length) * 100
  );

  const isLastStep =
    step === STEPS.length - 1;

  const submitLabel = saving
    ? "Saving…"
    : isEdit
      ? "Save changes"
      : form.status === "active"
        ? "Publish product"
        : "Create product";

  /* ---------- Step content ---------- */

  const renderBasics = () => (
    <div className="pf-grid">
      <Field
        id="pf-product-name"
        label="Product name"
        required
        className="pf-span-2"
        hint="This is what customers see, e.g. “Royal Gold Foil Wedding Invitation”."
        error={getFieldError("product_name")}
      >
        <input
          id="pf-product-name"
          name="product_name"
          value={form.product_name}
          onChange={handleChange}
          onBlur={() => touch("product_name")}
          placeholder="e.g. Royal Gold Foil Wedding Invitation"
          autoFocus={
            !isEdit && !form.product_name
          }
        />
      </Field>

      <Field
        id="pf-sku"
        label="SKU"
        required
        className="pf-span-2"
        hint={
          skuTouched
            ? "A unique code for stock and orders."
            : "Auto-filled from the name. Edit it or keep it as it is."
        }
        error={getFieldError("sku")}
      >
        <div className="pf-input-action">
          <input
            id="pf-sku"
            name="sku"
            value={form.sku}
            onChange={handleChange}
            onBlur={() => touch("sku")}
            placeholder="e.g. ROY-GOL-FOI-1024"
          />

          <button
            type="button"
            className="pf-inline-button"
            onClick={regenerateSku}
            disabled={!form.product_name.trim()}
            title="Generate a SKU from the product name"
          >
            <FiRefreshCw /> Generate
          </button>
        </div>
      </Field>

      <Field
        id="pf-category"
        label="Category"
        required
        hint={
          categories.length
            ? "Where this product appears in the store."
            : "No categories yet. Create one under Categories first."
        }
        error={getFieldError("category_id")}
      >
        <select
          id="pf-category"
          name="category_id"
          value={form.category_id}
          onChange={handleChange}
          onBlur={() => touch("category_id")}
        >
          <option value="">
            Select category
          </option>

          {categories.map((category) => (
            <option
              key={category.category_id}
              value={category.category_id}
            >
              {category.name}
            </option>
          ))}
        </select>
      </Field>

      <Field
        id="pf-sub-category"
        label="Sub-category"
        hint={
          form.category_id &&
          filteredSubCategories.length === 0
            ? "This category has no sub-categories."
            : "Narrows down where the product is listed."
        }
      >
        <select
          id="pf-sub-category"
          name="sub_category_id"
          value={form.sub_category_id}
          onChange={handleChange}
          disabled={
            !form.category_id ||
            filteredSubCategories.length === 0
          }
        >
          <option value="">
            {form.category_id
              ? "Select sub-category"
              : "Select category first"}
          </option>

          {filteredSubCategories.map(
            (subCategory) => (
              <option
                key={
                  subCategory.sub_category_id
                }
                value={
                  subCategory.sub_category_id
                }
              >
                {subCategory.name}
              </option>
            )
          )}
        </select>
      </Field>

      <Field
        id="pf-short-description"
        label="Short description"
        className="pf-span-2"
        hint={`One or two lines shown under the name in listings. ${form.short_description.length}/200 characters.`}
      >
        <textarea
          id="pf-short-description"
          name="short_description"
          value={form.short_description}
          onChange={handleChange}
          rows="3"
          maxLength={200}
          placeholder="e.g. Elegant ivory card with hand-pressed gold foil detailing."
        />
      </Field>
    </div>
  );

  const renderMediaItem = ({
    key,
    url,
    isVideo,
    isCover,
    isNew,
    name,
    onMakeCover,
    onRemove,
  }) => (
    <div
      key={key}
      className={`pf-media-item ${
        isCover ? "is-cover" : ""
      }`}
    >
      {isVideo ? (
        <video
          src={url}
          muted
          playsInline
          preload="metadata"
        />
      ) : (
        <img
          src={url}
          alt={name || form.product_name}
          loading="lazy"
        />
      )}

      <div className="pf-media-badges">
        {isCover && (
          <span className="pf-badge is-cover">
            <FiStar /> Cover
          </span>
        )}

        {isVideo && (
          <span className="pf-badge">
            <FiVideo /> Video
          </span>
        )}

        {isNew && (
          <span className="pf-badge is-new">
            New
          </span>
        )}
      </div>

      <div className="pf-media-actions">
        {onMakeCover && !isVideo && !isCover && (
          <button
            type="button"
            onClick={onMakeCover}
          >
            <FiStar /> Make cover
          </button>
        )}

        <button
          type="button"
          className="is-danger"
          onClick={onRemove}
          aria-label="Remove file"
          title="Remove"
        >
          <FiTrash2 />
        </button>
      </div>
    </div>
  );

  const renderMedia = () => (
    <>
      <div
        className={`pf-dropzone ${
          dragActive ? "is-dragging" : ""
        }`}
        role="button"
        tabIndex={0}
        onClick={() =>
          fileInputRef.current?.click()
        }
        onKeyDown={(event) => {
          if (
            event.key === "Enter" ||
            event.key === " "
          ) {
            event.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() =>
          setDragActive(false)
        }
        onDrop={handleDrop}
      >
        <span className="pf-dropzone-icon">
          <FiUploadCloud />
        </span>

        <strong>
          {dragActive
            ? "Drop to add"
            : "Drag photos & videos here"}
        </strong>

        <span>
          or{" "}
          <u>browse your files</u> · JPG,
          PNG, WEBP, MP4, MOV, WEBM
        </span>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={MEDIA_ACCEPT}
        onChange={handleFileInput}
        hidden
      />

      <ul className="pf-tips">
        <li>
          Use bright, well-lit photos on a
          plain background.
        </li>
        <li>
          Add close-ups of textures, foil and
          envelopes.
        </li>
        <li>
          Large images are compressed
          automatically when saved.
        </li>
      </ul>

      {totalMediaCount > 0 ? (
        <div className="pf-media-grid">
          {form.existingMedia.map((media) =>
            renderMediaItem({
              key: `saved-${media.image_id}`,
              url: media.image_url,
              isVideo: isVideoMedia(media),
              isCover:
                media.image_id ===
                existingCoverId,
              isNew: false,
              onMakeCover: () =>
                setPrimaryImage(
                  media.image_id
                ),
              onRemove: () =>
                markMediaForDelete(media),
            })
          )}

          {newMedia.map((item) =>
            renderMediaItem({
              key: item.id,
              url: item.url,
              isVideo: item.isVideo,
              isCover: item.id === newCoverId,
              isNew: true,
              name: item.file.name,
              onMakeCover: hasExistingImages
                ? null
                : () =>
                    makeNewMediaCover(item.id),
              onRemove: () =>
                removeNewMedia(item.id),
            })
          )}
        </div>
      ) : (
        <div className="pf-empty-note">
          <FiImage />
          No media yet. Products with photos
          get far more attention.
        </div>
      )}

      {removedMedia.length > 0 && (
        <div className="pf-removed-strip">
          <span>
            {removedMedia.length} saved file
            {removedMedia.length > 1
              ? "s"
              : ""}{" "}
            will be removed when you save.
          </span>

          <div className="pf-removed-list">
            {removedMedia.map((media) => (
              <button
                key={media.image_id}
                type="button"
                onClick={() =>
                  restoreMedia(media)
                }
                title="Undo removal"
              >
                {isVideoMedia(media) ? (
                  <FiVideo />
                ) : (
                  <img
                    src={media.image_url}
                    alt=""
                  />
                )}
                <FiRotateCcw /> Undo
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );

  const renderPricing = () => (
    <div className="pf-pricing">
      <div className="pf-grid">
        <Field
          id="pf-base-price"
          label="Base price"
          required
          hint="The regular price before any discount."
          error={getFieldError("base_price")}
        >
          <div className="pf-affix">
            <span>₹</span>

            <input
              id="pf-base-price"
              name="base_price"
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={form.base_price}
              onChange={handleChange}
              onBlur={() => touch("base_price")}
              placeholder="0.00"
              style={{paddingLeft: "2em"}}
            />
          </div>
        </Field>

        <Field
          id="pf-tax-class"
          label="HSN / SAC tax class"
          required
          hint={
            taxClasses.length
              ? "Decides the GST rate on invoices."
              : "No active tax classes. Add one under Tax Classes first."
          }
          error={getFieldError("tax_class_id")}
        >
          <select
            id="pf-tax-class"
            name="tax_class_id"
            value={form.tax_class_id}
            onChange={handleChange}
            onBlur={() => touch("tax_class_id")}
          >
            <option value="">
              Select tax class
            </option>

            {taxClasses.map((taxClass) => (
              <option
                key={taxClass.tax_class_id}
                value={taxClass.tax_class_id}
              >
                {taxClass.classification_type ===
                "service"
                  ? "SAC"
                  : "HSN"}{" "}
                {taxClass.hsn_sac_code} —{" "}
                {taxClass.name} (
                {Number(
                  taxClass.gst_percentage || 0
                )}
                % GST)
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="Discount"
          className="pf-span-2"
          hint="Optional. Show customers a reduced price."
        >
          <Segmented
            ariaLabel="Discount type"
            value={form.discount_type}
            onChange={(value) =>
              setField("discount_type", value)
            }
            options={[
              { value: "none", label: "No discount" },
              { value: "percentage", label: "% Percentage" },
              { value: "flat", label: "₹ Flat amount" },
            ]}
          />
        </Field>

        {form.discount_type !== "none" && (
          <Field
            id="pf-discount-value"
            label={
              form.discount_type === "percentage"
                ? "Discount percentage"
                : "Discount amount"
            }
            required
            className="pf-span-2"
            error={getFieldError(
              "discount_value"
            )}
          >
            <div className="pf-discount-row">
              <div className="pf-affix">
                <span>
                  {form.discount_type ===
                  "percentage"
                    ? "%"
                    : "₹"}
                </span>

                <input
                  id="pf-discount-value"
                  name="discount_value"
                  type="number"
                  min="0"
                  max={
                    form.discount_type ===
                    "percentage"
                      ? "100"
                      : undefined
                  }
                  step="0.01"
                  inputMode="decimal"
                  value={form.discount_value}
                  onChange={handleChange}
                  onBlur={() =>
                    touch("discount_value")
                  }
                  placeholder="0"
                  style={{paddingLeft: "2em"}}
                />
              </div>

              {form.discount_type ===
                "percentage" && (
                <div className="pf-chips">
                  {PERCENT_SHORTCUTS.map(
                    (percent) => (
                      <button
                        key={percent}
                        type="button"
                        className={
                          Number(
                            form.discount_value
                          ) === percent
                            ? "is-active"
                            : ""
                        }
                        onClick={() =>
                          setField(
                            "discount_value",
                            percent
                          )
                        }
                      >
                        {percent}%
                      </button>
                    )
                  )}
                </div>
              )}
            </div>
          </Field>
        )}
      </div>

      <div className="pf-price-summary">
        <div>
          <span>Base price</span>
          <strong>
            {formatPrice(basePriceNumber)}
          </strong>
        </div>

        <div>
          <span>Discount</span>
          <strong
            className={
              savings > 0 ? "is-saving" : ""
            }
          >
            {savings > 0
              ? `− ${formatPrice(savings)}`
              : "—"}
          </strong>
        </div>

        <div className="is-total">
          <span>Customer pays</span>
          <strong>
            {formatPrice(calculatedFinalPrice)}
          </strong>

          {savingsPercent > 0 && (
            <small>
              Saves {savingsPercent}%
            </small>
          )}
        </div>

        {selectedTaxClass && (
          <p className="pf-price-note">
            GST rate for this product:{" "}
            {Number(
              selectedTaxClass.gst_percentage ||
                0
            )}
            % ({selectedTaxClass.name})
          </p>
        )}
      </div>
    </div>
  );

  const renderOptions = () => (
    <>
      {optionTemplates.length === 0 ? (
        <div className="pf-empty-note">
          <FiLayers />
          No option templates yet. You can
          create them under Option Templates and
          attach them later. This step is
          optional.
        </div>
      ) : (
        <>
          <p className="pf-section-label">
            Choose which options customers can
            pick for this product
          </p>

          <div className="pf-template-grid">
            {optionTemplates.map((template) => {
              const checked =
                form.template_ids.includes(
                  String(template.template_id)
                );

              const valueCount = (
                template.fields || []
              ).reduce(
                (sum, field) =>
                  sum +
                  (field.values || []).length,
                0
              );

              return (
                <label
                  key={template.template_id}
                  className={`pf-template-card ${
                    checked ? "is-selected" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      toggleTemplate(
                        template.template_id
                      )
                    }
                  />

                  <span
                    className="pf-template-check"
                    aria-hidden="true"
                  >
                    {checked && <FiCheck />}
                  </span>

                  <span className="pf-template-body">
                    <strong>
                      {template.template_name}
                    </strong>

                    <small>
                      {(template.fields || [])
                        .length}{" "}
                      field
                      {(template.fields || [])
                        .length === 1
                        ? ""
                        : "s"}{" "}
                      · {valueCount} choice
                      {valueCount === 1
                        ? ""
                        : "s"}
                    </small>

                    {template.fields?.length >
                      0 && (
                      <span className="pf-template-fields">
                        {template.fields.map(
                          (field) => (
                            <em
                              key={
                                field.field_id
                              }
                            >
                              {field.field_name}
                            </em>
                          )
                        )}
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        </>
      )}

      {selectedTemplates.length > 0 && (
        <>
          <p className="pf-section-label">
            Prices for this product{" "}
            <span>
              Template defaults are used unless
              you change them here. Other
              products are not affected.
            </span>
          </p>

          <div className="pf-config-list">
            {selectedTemplates.map(
              (template) => {
                const key = String(
                  template.template_id
                );

                const expanded =
                  expandedTemplates.has(key);

                const stats =
                  getTemplateStats(template);

                return (
                  <div
                    key={key}
                    className={`pf-config ${
                      expanded
                        ? "is-open"
                        : ""
                    }`}
                  >
                    <button
                      type="button"
                      className="pf-config-toggle"
                      aria-expanded={expanded}
                      onClick={() =>
                        toggleTemplateExpanded(
                          key
                        )
                      }
                    >
                      <span>
                        <strong>
                          {
                            template.template_name
                          }
                        </strong>

                        <small>
                          {stats.total} choices ·{" "}
                          {stats.customised > 0
                            ? `${stats.customised} customised`
                            : "using template defaults"}
                        </small>
                      </span>

                      <span className="pf-config-cta">
                        {expanded
                          ? "Hide"
                          : "Customise"}
                        <FiChevronDown />
                      </span>
                    </button>

                    {expanded && (
                      <div className="pf-config-body">
                        {(
                          template.fields || []
                        ).map((field) => (
                          <div
                            key={field.field_id}
                            className="pf-config-field"
                          >
                            <h4>
                              {field.field_name}
                            </h4>

                            <div className="pf-config-table">
                              <div className="pf-config-head">
                                <span>
                                  Choice
                                </span>
                                <span>
                                  Extra price
                                </span>
                                <span>
                                  Available
                                </span>
                              </div>

                              {(
                                field.values ||
                                []
                              ).map((value) => {
                                const override =
                                  getOverrideForValue(
                                    value
                                  );

                                const defaultPrice =
                                  getTemplateDefaultPrice(
                                    value
                                  );

                                const available =
                                  override.is_available !==
                                  false;

                                return (
                                  <div
                                    key={
                                      value.value_id
                                    }
                                    className={`pf-config-row ${
                                      available
                                        ? ""
                                        : "is-off"
                                    }`}
                                  >
                                    <span className="pf-config-name">
                                      <strong>
                                        {
                                          value.label
                                        }
                                      </strong>
                                      <small>
                                        Default +
                                        {formatPrice(
                                          defaultPrice
                                        )}
                                      </small>
                                    </span>

                                    <div className="pf-affix is-small">
                                      <span>
                                        +₹
                                      </span>

                                      <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        inputMode="decimal"
                                        aria-label={`Extra price for ${value.label}`}
                                        value={
                                          override.price_adjustment
                                        }
                                        disabled={
                                          !available
                                        }
                                        onChange={(
                                          event
                                        ) =>
                                          updateOverride(
                                            value.value_id,
                                            {
                                              price_adjustment:
                                                event
                                                  .target
                                                  .value,
                                            },
                                            defaultPrice
                                          )
                                        }
                                      />
                                    </div>

                                    <label className="pf-mini-switch">
                                      <input
                                        type="checkbox"
                                        role="switch"
                                        aria-label={`${value.label} available`}
                                        checked={
                                          available
                                        }
                                        onChange={(
                                          event
                                        ) =>
                                          updateOverride(
                                            value.value_id,
                                            {
                                              is_available:
                                                event
                                                  .target
                                                  .checked,
                                            },
                                            defaultPrice
                                          )
                                        }
                                      />
                                      <span aria-hidden="true" />
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}

                        <button
                          type="button"
                          className="pf-link-button"
                          onClick={() =>
                            resetTemplateOverrides(
                              template
                            )
                          }
                          disabled={
                            stats.customised === 0
                          }
                        >
                          <FiRotateCcw /> Reset to
                          template defaults
                        </button>
                      </div>
                    )}
                  </div>
                );
              }
            )}
          </div>
        </>
      )}
    </>
  );

  const renderDetails = () => (
    <div className="pf-grid">
      <Field
        id="pf-description"
        label="Full description"
        className="pf-span-4"
        hint="Shown on the product page. Mention paper, size, printing method and what's included."
      >
        <textarea
          id="pf-description"
          name="description"
          value={form.description}
          onChange={handleChange}
          rows="6"
          placeholder="Describe the design, materials and finish…"
        />
      </Field>

      <Field
        id="pf-keywords"
        label="Search keywords"
        className="pf-span-2"
        hint="Words shoppers might search for. Press Enter or comma after each one."
      >
        <TagInput
          id="pf-keywords"
          value={form.keywords}
          onChange={(value) =>
            setField("keywords", value)
          }
          placeholder="wedding, invite, gold foil"
        />
      </Field>

      <Field
        id="pf-tags"
        label="Tags"
        className="pf-span-2"
        hint="Group similar products, e.g. premium, modern, minimal."
      >
        <TagInput
          id="pf-tags"
          value={form.tags}
          onChange={(value) =>
            setField("tags", value)
          }
          placeholder="premium, modern, minimal"
        />
      </Field>

      <details
        className="pf-advanced pf-span-4"
        open={advancedOpen}
        onToggle={(event) =>
          setAdvancedOpen(
            event.currentTarget.open
          )
        }
      >
        <summary>
          <span>
            <strong>
              Extra content blocks (HTML)
            </strong>
            <small>
              Optional tabs on the product page,
              such as specifications or care
              instructions.
            </small>
          </span>
          <FiChevronDown />
        </summary>

        <div className="pf-grid">
          <Field
            id="pf-product-info"
            label="Product info"
            className="pf-span-2"
          >
            <textarea
              id="pf-product-info"
              name="product_info"
              value={form.product_info}
              onChange={handleChange}
              rows="5"
              placeholder="<p>Size: 5 × 7 in</p>"
            />
          </Field>

          <Field
            id="pf-additional-info"
            label="Additional info"
            className="pf-span-2"
          >
            <textarea
              id="pf-additional-info"
              name="additional_info"
              value={form.additional_info}
              onChange={handleChange}
              rows="5"
              placeholder="<p>Delivery in 7–10 days</p>"
            />
          </Field>
        </div>
      </details>
    </div>
  );

  const renderReview = () => (
    <>
      <p className="pf-section-label">
        Final check
      </p>

      <ul className="pf-checklist">
        {checklist.map((item) => (
          <li
            key={item.label}
            className={
              item.done
                ? "is-done"
                : item.required
                  ? "is-missing"
                  : ""
            }
          >
            {item.done ? (
              <FiCheckCircle />
            ) : item.required ? (
              <FiAlertCircle />
            ) : (
              <FiCircle />
            )}

            <span>
              {item.label}
              {!item.required && (
                <small> Recommended</small>
              )}
            </span>

            {!item.done && (
              <button
                type="button"
                className="pf-link-button"
                onClick={() =>
                  goToStep(item.step)
                }
              >
                {item.required
                  ? "Fix now"
                  : "Add"}
              </button>
            )}
          </li>
        ))}
      </ul>

      <p className="pf-section-label">
        Who can see this product?
      </p>

      <div
        className="pf-status-grid"
        role="radiogroup"
        aria-label="Product status"
      >
        {STATUS_CHOICES.map((choice) => (
          <button
            key={choice.value}
            type="button"
            role="radio"
            aria-checked={
              form.status === choice.value
            }
            className={`pf-status-card is-${choice.value} ${
              form.status === choice.value
                ? "is-selected"
                : ""
            }`}
            onClick={() =>
              setField("status", choice.value)
            }
          >
            <span className="pf-status-dot" />
            <strong>{choice.title}</strong>
            <small>{choice.description}</small>
          </button>
        ))}
      </div>

      <div className="pf-grid">
        <Field
          label="Stock"
          className="pf-span-4"
        >
          <Segmented
            ariaLabel="Stock status"
            value={form.stock_status}
            onChange={(value) =>
              setField("stock_status", value)
            }
            options={[
              { value: "in_stock", label: "In stock" },
              { value: "out_of_stock", label: "Out of stock" },
            ]}
          />
        </Field>
      </div>

      <div className="pf-switch-list">
        <SwitchRow
          name="is_printable"
          checked={form.is_printable}
          onChange={handleChange}
          title="Printable product"
          description="Turn on if this item is printed to order and goes to the production workflow."
        />

        <SwitchRow
          name="requires_order_questionnaire"
          checked={
            form.requires_order_questionnaire
          }
          onChange={handleChange}
          title="Ask for wedding details after payment"
          description="Customers fill in names, dates and venue after they order."
        />
      </div>
    </>
  );

  const stepRenderers = {
    basics: renderBasics,
    media: renderMedia,
    pricing: renderPricing,
    options: renderOptions,
    details: renderDetails,
    review: renderReview,
  };

  if (pageLoading) {
    return (
      <section className="admin-page">
        <div className="page-card form-loader-card">
          <div className="premium-loader" />

          <h2>Loading product</h2>

          <p>
            Please wait while media
            and details are prepared.
          </p>
        </div>
      </section>
    );
  }

  const CurrentIcon = STEPS[step].icon;

  return (
    <section className="admin-page pf-page">
      <div className="page-header">
        <div>
          <p>Catalogue</p>

          <h1>
            {isEdit
              ? "Edit Product"
              : "Add Product"}
          </h1>
        </div>

        <button
          type="button"
          className="secondary-action"
          onClick={() =>
            navigate("/products")
          }
        >
          Back to Products
        </button>
      </div>

      <nav
        className="pf-stepper"
        aria-label="Product form steps"
      >
        {STEPS.map((item, index) => {
          const state = getStepState(index);
          const Icon = item.icon;

          return (
            <button
              key={item.key}
              type="button"
              className={`pf-step is-${state}`}
              aria-current={
                state === "current"
                  ? "step"
                  : undefined
              }
              onClick={() => goToStep(index)}
            >
              <span className="pf-step-marker">
                {state === "done" ? (
                  <FiCheck />
                ) : state === "error" ? (
                  <FiAlertCircle />
                ) : (
                  <Icon />
                )}
              </span>

              <span className="pf-step-text">
                <strong>{item.title}</strong>
                <small>{item.hint}</small>
              </span>
            </button>
          );
        })}
      </nav>

      <div className="pf-layout">
        <form
          className="pf-form page-card"
          onSubmit={handleFormSubmit}
          onKeyDown={handleFormKeyDown}
          noValidate
        >
          <header
            className="pf-step-header"
            ref={stepTopRef}
          >
            <span className="pf-step-icon">
              <CurrentIcon />
            </span>

            <div>
              <p>
                Step {step + 1} of {STEPS.length}
              </p>
              <h2>{STEPS[step].title}</h2>
              <span>
                {STEPS[step].description}
              </span>
            </div>
          </header>

          <div
            className="pf-step-body"
            key={STEPS[step].key}
          >
            {stepRenderers[STEPS[step].key]()}
          </div>

          <footer className="pf-footer">
            <div
              className="pf-footer-progress"
              aria-hidden="true"
            >
              <span
                style={{
                  width: `${
                    ((step + 1) / STEPS.length) *
                    100
                  }%`,
                }}
              />
            </div>

            <div className="pf-footer-actions">
              {step > 0 ? (
                <button
                  type="button"
                  className="secondary-action"
                  onClick={goBack}
                >
                  <FiArrowLeft /> Back
                </button>
              ) : (
                <button
                  type="button"
                  className="ghost-action"
                  onClick={() =>
                    navigate("/products")
                  }
                >
                  Cancel
                </button>
              )}

              <div className="pf-footer-right">
                {isEdit && !isLastStep && (
                  <button
                    type="button"
                    className="secondary-action"
                    onClick={submitProduct}
                    disabled={saving}
                  >
                    {saving
                      ? "Saving…"
                      : "Save changes"}
                  </button>
                )}

                {/*
                  * Distinct keys stop React from reusing the
                  * Next button as the submit button mid-click,
                  * which would save the product immediately.
                  */}
                {isLastStep ? (
                  <button
                    key="submit"
                    type="submit"
                    className="primary-action"
                    disabled={saving}
                  >
                    {submitLabel}
                  </button>
                ) : (
                  <button
                    key="next"
                    type="button"
                    className="primary-action"
                    onClick={goNext}
                  >
                    Next: {STEPS[step + 1].title}
                    <FiArrowRight />
                  </button>
                )}
              </div>
            </div>
          </footer>
        </form>

        <aside
          className="pf-aside"
          aria-label="Live preview"
        >
          <div className="pf-preview">
            <p className="pf-aside-title">
              Live preview
            </p>

            <div className="pf-preview-media">
              {coverUrl ? (
                <img
                  src={coverUrl}
                  alt={form.product_name}
                />
              ) : (
                <button
                  type="button"
                  className="pf-preview-empty"
                  onClick={() => goToStep(1)}
                >
                  <FiImage />
                  Add a cover photo
                </button>
              )}

              {savingsPercent > 0 && (
                <span className="pf-preview-sale">
                  −{savingsPercent}%
                </span>
              )}

              <span
                className={`pf-preview-status is-${form.status}`}
              >
                {form.status}
              </span>
            </div>

            <div className="pf-preview-body">
              <small className="pf-preview-category">
                {selectedCategory
                  ? [
                      selectedCategory.name,
                      selectedSubCategory?.name,
                    ]
                      .filter(Boolean)
                      .join(" › ")
                  : "No category yet"}
              </small>

              <h3
                className={
                  form.product_name.trim()
                    ? ""
                    : "is-placeholder"
                }
              >
                {form.product_name.trim() ||
                  "Untitled product"}
              </h3>

              {form.short_description.trim() && (
                <p>
                  {form.short_description}
                </p>
              )}

              <div className="pf-preview-price">
                <strong>
                  {formatPrice(
                    calculatedFinalPrice
                  )}
                </strong>

                {savings > 0 && (
                  <s>
                    {formatPrice(
                      basePriceNumber
                    )}
                  </s>
                )}
              </div>

              <div className="pf-preview-meta">
                <span>
                  {form.stock_status ===
                  "in_stock"
                    ? "In stock"
                    : "Out of stock"}
                </span>
                <span>
                  {totalMediaCount} media
                </span>
                <span>
                  {form.template_ids.length}{" "}
                  option set
                  {form.template_ids.length === 1
                    ? ""
                    : "s"}
                </span>
              </div>
            </div>
          </div>

          <div className="pf-progress-card">
            <div className="pf-progress-head">
              <p className="pf-aside-title">
                Listing quality
              </p>
              <strong>{progressPercent}%</strong>
            </div>

            <div
              className="pf-progress-bar"
              role="progressbar"
              aria-valuenow={progressPercent}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <span
                style={{
                  width: `${progressPercent}%`,
                }}
              />
            </div>

            <ul>
              {checklist.map((item) => (
                <li
                  key={item.label}
                  className={
                    item.done ? "is-done" : ""
                  }
                >
                  <button
                    type="button"
                    onClick={() =>
                      goToStep(item.step)
                    }
                  >
                    {item.done ? (
                      <FiCheckCircle />
                    ) : (
                      <FiCircle />
                    )}
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </section>
  );
};

export default ProductForm;
